import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import { paymentService } from "../../services/paymentService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { ArrowLeft, Pencil, Package, MoreVertical, Eye, Trash2, Printer } from "lucide-react";
import { PageLayout, Button, DetailCard, DetailRow, StatusBadge } from "../common/index.js";

const ViewShipment = () => {
  const { shipmentId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [shipment, setShipment] = useState(null)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false)

  const [showDeliveryChargeModal, setShowDeliveryChargeModal] = useState(false)
  const [showLabourChargeModal, setShowLabourChargeModal] = useState(false)
  const [showLateChargeModal, setShowLateChargeModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const [deliveryChargeAmount, setDeliveryChargeAmount] = useState("")
  const [labourChargeAmount, setLabourChargeAmount] = useState("")
  const [lateChargeAmount, setLateChargeAmount] = useState("")
  const [addingCharge, setAddingCharge] = useState(false)
  const [addingLateCharge, setAddingLateCharge] = useState(false)

  const [deliveryChargePaymentMode, setDeliveryChargePaymentMode] = useState("")
  const [labourChargePaymentMode, setLabourChargePaymentMode] = useState("")
  const [lateChargePaymentMode, setLateChargePaymentMode] = useState("")

  const [deletingPaymentId, setDeletingPaymentId] = useState(null)
  const [ewayBills, setEwayBills] = useState([])
  const [loadingEwayBills, setLoadingEwayBills] = useState(false)

  const isAdminRole = user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"

  const fetchShipmentDetails = useCallback(async () => {
    try {
      setLoading(true)
      const shipmentRes = await shipmentService.getShipment(shipmentId)
      setShipment(shipmentRes.data.shipment || shipmentRes.data.data)
    } catch (error) {
      console.error("Error fetching shipment:", error)
      Alert.error("Failed to load shipment details")
      navigate("/shipments")
    } finally {
      setLoading(false)
    }
  }, [shipmentId, navigate])

  const fetchPaymentHistory = useCallback(async () => {
    try {
      setLoadingPaymentHistory(true)
      const response = await paymentService.getPaymentHistory(shipmentId)
      // Fix: Access payment_history from nested data object
      const historyData = response.data?.data?.payment_history || response.data?.payment_history || []
      setPaymentHistory(historyData)
    } catch (error) {
      console.error("Error fetching payment history:", error)
      setPaymentHistory([])
    } finally {
      setLoadingPaymentHistory(false)
    }
  }, [shipmentId])

  const fetchEwayBills = useCallback(async () => {
    try {
      setLoadingEwayBills(true)
      const response = await shipmentService.getEwayBills(shipmentId)
      setEwayBills(response.data.eway_bills || [])
    } catch (error) {
      console.error("Error fetching e-way bills:", error)
      setEwayBills([])
    } finally {
      setLoadingEwayBills(false)
    }
  }, [shipmentId])

  useEffect(() => {
    fetchShipmentDetails()
    fetchPaymentHistory()
    fetchEwayBills()
  }, [fetchShipmentDetails, fetchPaymentHistory])

  const downloadInvoice = async () => {
    try {
      const response = await shipmentService.downloadInvoice(shipmentId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `invoice_${shipmentId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      Alert.success("Invoice downloaded successfully")
    } catch (error) {
      console.error("Error downloading invoice:", error)
      Alert.error("Failed to download invoice")
    }
  }

  const handlePrintReceipt = async () => {
    try {
      const response = await shipmentService.printReceipt(shipmentId)
      
      const printWindow = window.open('', '_blank', 'width=800,height=600')
      printWindow.document.write(response.data)
      printWindow.document.close()
      
      Alert.success("Receipt opened for printing")
    } catch (error) {
      console.error("Print receipt error:", error)
      Alert.error(error.response?.data?.error || "Failed to print receipt")
    }
  }

  const handleAddDeliveryCharge = async () => {
    const amount = Number.parseFloat(deliveryChargeAmount)

    if (isNaN(amount) || amount <= 0) {
      Alert.error("Please enter a valid delivery charge amount")
      return
    }

    if (!deliveryChargePaymentMode) {
      Alert.error("Please select a payment mode")
      return
    }

    const confirmed = await Alert.confirm(
      `Add delivery charge of ₹${amount.toFixed(2)} via ${deliveryChargePaymentMode.toUpperCase()} to shipment ${shipmentId}?`,
      "Confirm Delivery Charge",
    )

    if (!confirmed) return

    try {
      setAddingCharge(true)
      await shipmentService.addDeliveryCharge(shipmentId, amount, deliveryChargePaymentMode)

      Alert.success("Delivery charge added successfully")
      setShowDeliveryChargeModal(false)
      setDeliveryChargeAmount("")
      setDeliveryChargePaymentMode("")

      await fetchShipmentDetails()
      await fetchPaymentHistory()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to add delivery charge")
    } finally {
      setAddingCharge(false)
    }
  }

  const handleAddLabourCharge = async () => {
    const amount = Number.parseFloat(labourChargeAmount)

    if (isNaN(amount) || amount <= 0) {
      Alert.error("Please enter a valid labour charge amount")
      return
    }

    if (!labourChargePaymentMode) {
      Alert.error("Please select a payment mode")
      return
    }

    const confirmed = await Alert.confirm(
      `Add labour charge of ₹${amount.toFixed(2)} via ${labourChargePaymentMode.toUpperCase()} to shipment ${shipmentId}?`,
      "Confirm Labour Charge",
    )

    if (!confirmed) return

    try {
      setAddingCharge(true)
      await shipmentService.addLabourCharge(shipmentId, amount, labourChargePaymentMode)

      Alert.success("Labour charge added successfully")
      setShowLabourChargeModal(false)
      setLabourChargeAmount("")
      setLabourChargePaymentMode("")

      await fetchShipmentDetails()
      await fetchPaymentHistory()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to add labour charge")
    } finally {
      setAddingCharge(false)
    }
  }

  const handleAddLateCharge = async () => {
    const amount = Number.parseFloat(lateChargeAmount)

    if (isNaN(amount) || amount <= 0) {
      Alert.error("Please enter a valid late charge amount")
      return
    }

    if (!lateChargePaymentMode) {
      Alert.error("Please select a payment mode")
      return
    }

    const confirmed = await Alert.confirm(
      `Add late charge of ₹${amount.toFixed(2)} via ${lateChargePaymentMode.toUpperCase()} to shipment ${shipmentId}?`,
      "Confirm Late Charge",
    )

    if (!confirmed) return

    try {
      setAddingLateCharge(true)
      await paymentService.addLateCharge(shipmentId, amount, lateChargePaymentMode)

      Alert.success("Late charge added successfully")
      setShowLateChargeModal(false)
      setLateChargeAmount("")
      setLateChargePaymentMode("")

      await fetchShipmentDetails()
      await fetchPaymentHistory()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to add late charge")
    } finally {
      setAddingLateCharge(false)
    }
  }

  const handleDeletePayment = async (payment) => {
    const confirmed = await Alert.confirm(
      `Delete payment record of ₹${payment.payment_amount?.toFixed(2)} for shipment ${shipmentId}?`,
      "Delete Payment Record"
    )
    if (!confirmed) return

    try {
      setDeletingPaymentId(payment.id)
      await paymentService.deletePayment(payment.id)
      Alert.success("Payment record deleted successfully")
      await fetchShipmentDetails()
      await fetchPaymentHistory()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to delete payment")
    } finally {
      setDeletingPaymentId(null)
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      Booked: "bg-blue-100 text-blue-800 border-blue-300",
      Dispatched: "bg-purple-100 text-purple-800 border-purple-300",
      Received: "bg-yellow-100 text-yellow-800 border-yellow-300",
      Delivered: "bg-green-100 text-green-800 border-green-300",
      Returned: "bg-red-100 text-red-800 border-red-300",
      Rejected: "bg-gray-100 text-gray-800 border-gray-300",
    }
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-300"
  }

  if (loading) {
    return (
      <PageLayout title="Shipment Details" icon={Package}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brand mb-4"></div>
            <p className="text-gray-600 font-semibold">Loading shipment details...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!shipment) {
    return (
      <PageLayout title="Shipment Details" icon={Package}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <p className="text-gray-600 font-semibold text-lg">Shipment not found</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  const isLabourChargeDisabled = false
  const isDeliveryChargeDisabled = false

  return (
    <PageLayout
      title="Shipment Details"
      icon={Package}
      maxWidth="max-w-6xl"
      actions={
        <>
          {isAdminRole && (
            <Button variant="outline" icon={Pencil} onClick={() => navigate(`/shipments/edit/${shipmentId}`)}>
              Edit
            </Button>
          )}
          <Button variant="outline" icon={Printer} onClick={handlePrintReceipt}>
            Print Receipt
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/shipments')}>
            Back
          </Button>
          <div className="relative group">
            <Button icon={MoreVertical}>More Actions</Button>
            <div className="absolute right-0 mt-0 w-48 bg-white shadow-lg rounded-card border border-gray-200 hidden group-hover:block z-50">
              <button onClick={downloadInvoice} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition">
                Download Invoice
              </button>
              <button onClick={() => setShowDeliveryChargeModal(true)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition">
                Add Delivery Charge
              </button>
              <button onClick={() => setShowLabourChargeModal(true)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition">
                Add Labour Charge
              </button>
              <button onClick={() => setShowLateChargeModal(true)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition">
                Add Late Charge
              </button>
            </div>
          </div>
        </>
      }
    >
                {/* Status Cards */}
                <div className="mb-4 sm:mb-6">

                  {/* FLEX WRAPPER FOR DESKTOP */}
                  <div className="bg-white grid grid-cols-1 lg:grid-cols-4 gap-4">

                    {/* Shipment ID */}
                    <div className="bg-white rounded-sm shadow-md p-3 sm:p-5 border-l-4 border-brand">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Shipment ID
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-blue-600 font-mono">
                        {shipment.shipment_id}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="bg-white rounded-sm shadow-md p-3 sm:p-5 border-l-4 border-brand">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Status
                      </p>
                      <span
                        className={`inline-block px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold uppercase border ${getStatusColor(
                          shipment.status
                        )}`}
                      >
                        {shipment.status}
                      </span>
                    </div>

                    {/* Total Amount */}
                    <div className="bg-white rounded-sm shadow-md p-3 sm:p-5 border-l-4 border-green-500">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Total Amount
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-green-600">
                        ₹{shipment.total_amount?.toFixed(2)}
                      </p>
                    </div>

                    {/* Balance Due */}
                    <div className="bg-white rounded-sm shadow-md p-3 sm:p-5 border-l-4 border-red-500">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Balance Due
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-red-600">
                        ₹{shipment.balance_amount?.toFixed(2)}
                      </p>
                    </div>

                  </div>
                </div>


                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-6">
                  {/* Consigner Information */}
                  <div className="bg-white rounded-sm shadow-md p-4 sm:p-5">
                    <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Consigner Information
                    </h2>
                    <div className="space-y-2 sm:space-y-2 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                      <div className="mt-2">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Name</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consigner_name || shipment.sender_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Contact</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consigner_contact || shipment.sender_contact}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Email</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consigner_email || shipment.sender_email || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Destination</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consigner_location || shipment.sender_location || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Consigner GST</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consigner_gst || shipment.sender_gst || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="mt-8">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Address</p>
                        <p className="font-medium text-tertiary text-sx sm:text-base bg-gray-100 p-3 py-1 mt-1">
                          {shipment.consigner_address || shipment.sender_address}
                        </p>
                      </div>
                  </div>

                  {/* Consignee Information */}
                  <div className="bg-white rounded-sm shadow-md p-4 sm:p-5">
                    <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Consignee Information
                    </h2>
                    <div className="space-y-2 sm:space-y-2 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Name</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consignee_name || shipment.receiver_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Contact</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consignee_contact || shipment.receiver_contact}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Email</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consignee_email || shipment.receiver_email || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Destination</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consignee_location || shipment.receiver_location || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Consignee GST</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consignee_gst || shipment.receiver_gst || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="mt-8">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Address</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                          {shipment.consignee_address || shipment.receiver_address}
                        </p>
                      </div>
                  </div>
                </div>

                <div className="bg-white rounded-sm shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2">
                      Payment History
                    </h2>
                    <button
                      onClick={() => navigate(`/payment-history/add/${shipmentId}`)}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      Add Payment
                    </button>
                  </div>

                  {loadingPaymentHistory ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand"></div>
                      <p className="text-gray-600 font-semibold mt-2">Loading payment history...</p>
                    </div>
                  ) : paymentHistory && paymentHistory.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">Recorded By</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-700">Amount</th>
                            <th className="px-3 py-2 text-right font-semibold text-gray-700">Balance</th>
                            <th className="px-3 py-2 text-left font-semibold text-gray-700">Remark</th>
                            <th className="px-3 py-2 text-center font-semibold text-gray-700">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {paymentHistory.map((payment, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-900 font-semibold">
                                {payment.recorded_by_name || "N/A"}
                              </td>
                              <td className="px-3 py-2">
                                <span className="px-2 py-1 rounded-full text-xs font-semibold text-secondary">
                                  {payment.payment_mode?.replace(/_/g, ' ') || payment.type || "Payment"}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-900">
                                {new Date(payment.payment_date || payment.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-green-600">
                                ₹{payment.payment_amount?.toFixed(2) || payment.amount?.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right font-bold text-blue-600">
                                ₹{payment.new_balance?.toFixed(2) || payment.balance?.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-gray-700 text-xs">
                                {payment.remarks || payment.remark || "—"}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => navigate(`/payment-history/view/${payment.id}`)}
                                    className="p-1.5 rounded text-brand hover:bg-brand-light transition"
                                    title="View Payment"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => navigate(`/payment-history/edit/${payment.id}`)}
                                    className="p-1.5 rounded text-green-600 hover:bg-green-50 transition"
                                    title="Edit Payment"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeletePayment(payment)}
                                    disabled={deletingPaymentId === payment.id}
                                    className="p-1.5 rounded text-red-600 hover:bg-red-50 transition disabled:opacity-40"
                                    title="Delete Payment"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-5xl mb-4">📭</div>
                      <p className="text-gray-600 font-semibold mb-4">No payment history found</p>
                      <button
                        onClick={() => navigate(`/payment-history/add/${shipmentId}`)}
                        className="group items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                      >
                        Add First Payment
                      </button>
                    </div>
                  )}
                </div>

                {/* Package & Payment Information */}
                <div className="bg-white rounded-sm shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
                  <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                    Article & Payment Information
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-5">
                    {/* Article Information */}
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Number of Articles</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">{shipment.no_of_articles || 1}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Article Type</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1 capitalize">
                        {shipment.article_type || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Article Amount</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">₹{shipment.article_amount?.toFixed(2) || "0.00"}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Weight</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">{shipment.weight || "N/A"} kg</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Shipment Value</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">₹{shipment.shipment_value?.toFixed(2) || "0.00"}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Invoice No.</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">{shipment.invoice_number|| "N/A"}</p>
                    </div>

                    {/* Charges Breakdown */}
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Freight Charges</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.freight_charges?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Handling Charges</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.handling_charges?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Delivery Charges</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.delivery_charges?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Door Pickup Charges</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.door_pickup_charges?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Docket Fee</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.docket_fee?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Late Charges</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.late_charges?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Other Charges</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.other_charges?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Discount</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">-₹{shipment.discount?.toFixed(2) || "0.00"}</p>
                    </div>

                    {/* GST Information */}
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">SGST ({shipment.sgst_percentage || 0}%)</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.sgst_amount?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">CGST ({shipment.cgst_percentage || 0}%)</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.cgst_amount?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">IGST ({shipment.igst_percentage || 0}%)</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        ₹{shipment.igst_amount?.toFixed(2) || "0.00"}
                      </p>
                    </div>

                    {/* Payment Summary */}
                    <div className="bg-brand-light p-2 rounded-sm">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Total Amount</p>
                      <p className="font-bold text-brand text-sm sm:text-base mt-1">₹{shipment.total_amount?.toFixed(2) || "0.00"}</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded-sm">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Paid Amount</p>
                      <p className="font-bold text-green-600 text-sm sm:text-base mt-1">₹{shipment.paid_amount?.toFixed(2) || "0.00"}</p>
                    </div>
                    <div className="bg-red-50 p-2 rounded-sm">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Balance Due</p>
                      <p className="font-bold text-red-600 text-sm sm:text-base mt-1">₹{shipment.balance_amount?.toFixed(2) || "0.00"}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Payment Status</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1 uppercase">
                        {shipment.mode_of_payment?.replace(/_/g, " ") || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Booked By</p>
                      <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                        {shipment.created_by_name || shipment.created_by || "N/A"}
                      </p>
                    </div>
                    {shipment.updated_by_name && shipment.updated_by_name !== shipment.created_by_name && (
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Last Updated By</p>
                        <p className="font-medium text-tertiary text-sm sm:text-base mt-1">
                          {shipment.updated_by_name}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {(shipment.parcel_image_1_base64 || shipment.parcel_image_2_base64) && (
                  <div className="bg-white rounded-sm shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Parcel Images
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                      {shipment.parcel_image_1_base64 && (
                        <div>
                          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-2">Parcel Image 1</p>
                          <img
                            src={`data:image/jpeg;base64,${shipment.parcel_image_1_base64}`}
                            alt="Parcel 1"
                            className="w-full h-64 object-cover rounded-sm shadow-md"
                          />
                        </div>
                      )}
                      {shipment.parcel_image_2_base64 && (
                        <div>
                          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-2">Parcel Image 2</p>
                          <img
                            src={`data:image/jpeg;base64,${shipment.parcel_image_2_base64}`}
                            alt="Parcel 2"
                            className="w-full h-64 object-cover rounded-sm shadow-md"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* E-way Bills Section */}
                <div className="bg-white rounded-sm shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
                  <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                    E-way Bills
                  </h2>
                  {loadingEwayBills ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-brand"></div>
                      <p className="text-sm text-gray-600 mt-2">Loading E-way bills...</p>
                    </div>
                  ) : ewayBills.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {ewayBills.map((bill, index) => (
                        <div key={bill.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-sm p-3">
                          <p className="text-xs text-gray-600 font-semibold mb-1">E-way Bill #{index + 1}</p>
                          <p className="font-mono font-bold text-blue-800 text-sm">{bill.eway_bill_number}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Added: {new Date(bill.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">📋</div>
                      <p className="text-gray-600 text-sm">No E-way bills added for this shipment</p>
                    </div>
                  )}
                </div>

                {/* Delivery Charge Modal */}
                {showDeliveryChargeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Delivery Charge</h2>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Amount (₹) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={deliveryChargeAmount}
                              onChange={(e) => setDeliveryChargeAmount(e.target.value)}
                              placeholder="Enter amount"
                              className="px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Payment Mode *</label>
                            <select
                              value={deliveryChargePaymentMode}
                              onChange={(e) => setDeliveryChargePaymentMode(e.target.value)}
                              className="px-3 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-brand"
                            >
                              <option value="">Select Mode</option>
                              <option value="cash">Cash</option>
                              <option value="upi">UPI</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="cheque">Cheque</option>
                              <option value="online">Online</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => {
                            setShowDeliveryChargeModal(false)
                            setDeliveryChargeAmount("")
                            setDeliveryChargePaymentMode("")
                          }}
                          className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-sm hover:bg-gray-400 font-semibold"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddDeliveryCharge}
                          disabled={addingCharge}
                          className="flex-1 px-4 py-2 bg-brand text-white rounded-sm hover:bg-brand-hover font-semibold disabled:bg-gray-400"
                        >
                          {addingCharge ? "Adding..." : "Add Charge"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Labour Charge Modal */}
                {showLabourChargeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Labour Charge</h2>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Amount (₹) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={labourChargeAmount}
                              onChange={(e) => setLabourChargeAmount(e.target.value)}
                              placeholder="Enter amount"
                              className="px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-purple-500"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Payment Mode *</label>
                            <select
                              value={labourChargePaymentMode}
                              onChange={(e) => setLabourChargePaymentMode(e.target.value)}
                              className="px-3 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-purple-500"
                            >
                              <option value="">Select Mode</option>
                              <option value="cash">Cash</option>
                              <option value="upi">UPI</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="cheque">Cheque</option>
                              <option value="online">Online</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => {
                            setShowLabourChargeModal(false)
                            setLabourChargeAmount("")
                            setLabourChargePaymentMode("")
                          }}
                          className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-sm hover:bg-gray-400 font-semibold"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddLabourCharge}
                          disabled={addingCharge}
                          className="flex-1 px-4 py-2 bg-brand text-white rounded-sm hover:bg-brand-hover font-semibold disabled:bg-gray-400"
                        >
                          {addingCharge ? "Adding..." : "Add Charge"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Late Charge Modal */}
                {showLateChargeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Late Charge</h2>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Amount (₹) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={lateChargeAmount}
                              onChange={(e) => setLateChargeAmount(e.target.value)}
                              placeholder="Enter amount"
                              className="px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-red-500"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Payment Mode *</label>
                            <select
                              value={lateChargePaymentMode}
                              onChange={(e) => setLateChargePaymentMode(e.target.value)}
                              className="px-3 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-red-500"
                            >
                              <option value="">Select Mode</option>
                              <option value="cash">Cash</option>
                              <option value="upi">UPI</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="cheque">Cheque</option>
                              <option value="online">Online</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => {
                            setShowLateChargeModal(false)
                            setLateChargeAmount("")
                            setLateChargePaymentMode("")
                          }}
                          className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-sm hover:bg-gray-400 font-semibold"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddLateCharge}
                          disabled={addingLateCharge}
                          className="flex-1 px-4 py-2 bg-brand text-white rounded-sm hover:bg-brand-hover font-semibold disabled:bg-gray-400"
                        >
                          {addingLateCharge ? "Adding..." : "Add Charge"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showPaymentModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Payment</h2>
                      <p className="text-sm text-gray-600 mb-4">
                        Balance Due: <span className="font-bold text-red-600">₹{shipment.balance_amount?.toFixed(2)}</span>
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowPaymentModal(false)}
                          className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-sm hover:bg-gray-400 font-semibold"
                        >
                          Close
                        </button>
                        <button
                          onClick={() => navigate(`/payment-history/add/${shipmentId}`)}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 font-semibold"
                        >
                          Go to Payment Form
                        </button>
                      </div>
                    </div>
                  </div>
                )}
    </PageLayout>
  )
}

export default ViewShipment;